import { useState, useRef, useEffect, Fragment, type ReactNode } from 'react';
import { ChipGroup } from './ChipGroup';
import { DatePicker } from './DatePicker';
import './SearchFilterBar.css';

export interface FilterOption {
  value: string;
  label: string;
  /** Optional heading this option sits under. When any option in a `select`
   *  carries one, the control renders `<optgroup>` runs in option order — one
   *  level of nesting, which is all a native select has and all a long flat
   *  list needs. The same `value` may appear under two groups (a prompt type
   *  used by two operations); either picks the same filter. */
  group?: string;
}

interface BaseFilterDef {
  key: string;
}

export interface SelectFilterDef extends BaseFilterDef {
  kind?: 'select';
  placeholder: string;
  options: FilterOption[];
  /** When true, omit the empty placeholder option — value must always be one of `options`. */
  required?: boolean;
}

export interface TextFilterDef extends BaseFilterDef {
  kind: 'text';
  placeholder: string;
}

/** Multi-select dropdown — pick any subset of `options`. The selected values are
 *  stored as a comma-joined string under `values[key]` (empty string = none
 *  selected), so it fits the same `Record<string, string>` values map as every
 *  other filter. The trigger shows the placeholder when empty, the single label
 *  when one is chosen, or "N selected" otherwise. */
export interface MultiSelectFilterDef extends BaseFilterDef {
  kind: 'multiselect';
  placeholder: string;
  options: FilterOption[];
}

export interface DateFilterDef extends BaseFilterDef {
  kind: 'date';
  title?: string;
}

export interface DateTimeLocalFilterDef extends BaseFilterDef {
  kind: 'datetime-local';
  title?: string;
}

/** A from/to date range. Renders two `date` inputs; the selected values are
 *  stored under `${key}From` and `${key}To` in the values map (so the parent
 *  reads `values[`${key}From`]` / `values[`${key}To`]`). Either end may be left
 *  empty (open-ended range). */
export interface DateRangeFilterDef extends BaseFilterDef {
  kind: 'daterange';
  /** Optional label rendered before the inputs (e.g. "Assigned on"). */
  label?: string;
  fromTitle?: string;
  toTitle?: string;
  /** Visible placeholder text when a date is empty (default "Select date"). */
  fromPlaceholder?: string;
  toPlaceholder?: string;
}

/** Segmented chip group — pick one of N short options. Use for compact
 *  always-required filters (e.g. "7d / 30d / All") where the chip layout
 *  reads faster than a dropdown. The first option is the default; the
 *  filter value is always one of `options[i].value` (the chip group has
 *  no empty state). */
export interface ChipsFilterDef extends BaseFilterDef {
  kind: 'chips';
  options: FilterOption[];
  /** Optional label rendered to the left of the chip group. Omit for an
   *  inline group with no label. */
  label?: string;
}

export type FilterDef = SelectFilterDef | TextFilterDef | DateFilterDef | DateTimeLocalFilterDef | ChipsFilterDef | DateRangeFilterDef | MultiSelectFilterDef;

function filterKind(f: FilterDef): 'select' | 'text' | 'date' | 'datetime-local' | 'chips' | 'daterange' | 'multiselect' {
  return f.kind ?? 'select';
}

/**
 * Options, grouped into `<optgroup>` runs when they ask to be.
 *
 * Runs rather than a bucketing pass: the order the caller gave is the order
 * shown, and an option may legitimately appear under two groups, so collecting
 * by key would silently drop the second one.
 */
function renderOptions(options: FilterOption[]): ReactNode {
  if (!options.some(o => o.group)) {
    return options.map(o => <option key={o.value} value={o.value}>{o.label}</option>);
  }
  const runs: { group?: string; options: FilterOption[] }[] = [];
  for (const o of options) {
    const last = runs[runs.length - 1];
    if (last && last.group === o.group) last.options.push(o);
    else runs.push({ group: o.group, options: [o] });
  }
  return runs.map((run, i) => {
    const items = run.options.map(o => (
      <option key={`${run.group ?? ''}:${o.value}`} value={o.value}>{o.label}</option>
    ));
    return run.group
      ? <optgroup key={`${run.group}-${i}`} label={run.group}>{items}</optgroup>
      : <Fragment key={`ungrouped-${i}`}>{items}</Fragment>;
  });
}

/** Themed multi-select dropdown used by the `multiselect` filter kind. Value is a
 *  comma-joined string of selected option values; closes on outside click. */
function MultiSelectDropdown({ def, value, onChange }: {
  def: MultiSelectFilterDef;
  value: string;
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = value ? value.split(',').filter(Boolean) : [];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (v: string) => {
    const next = selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v];
    // Preserve option order for a stable serialized value.
    onChange(def.options.filter(o => next.includes(o.value)).map(o => o.value).join(','));
  };

  const label = selected.length === 0
    ? def.placeholder
    : selected.length === 1
      ? (def.options.find(o => o.value === selected[0])?.label ?? selected[0])
      : `${selected.length} selected`;

  return (
    <div className="sfb-multiselect" ref={ref}>
      <button
        type="button"
        className="sfb-multiselect-trigger"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selected.length === 0 ? 'sfb-multiselect-placeholder' : undefined}>{label}</span>
        <span className="sfb-multiselect-caret">▾</span>
      </button>
      {open && (
        <div className="sfb-multiselect-menu" role="listbox">
          {def.options.map(o => (
            <label key={o.value} className="sfb-multiselect-option">
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={() => toggle(o.value)}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export interface SearchFilterBarProps {
  filters: FilterDef[];
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
  searchPlaceholder?: string;
  hideSearch?: boolean;
  /** Extra inputs rendered at the START of the row, before the built-in filters
   *  (parent owns their state). Use for a control that reads as the primary
   *  scope of the view — e.g. a user picker — which belongs left of the
   *  filters that narrow within it, not after them like `children`. */
  leading?: ReactNode;
  /** Extra inputs rendered between the built-in filters and the Fetch button (parent owns their state). */
  children?: ReactNode;
  /** Extra inputs rendered immediately AFTER a named filter, keyed by that
   *  filter's `key`. Use when a custom control belongs beside the filter it
   *  depends on — e.g. a topic picker next to the subject select it is scoped
   *  by — rather than at the end of the row like `children`. */
  renderAfter?: Record<string, ReactNode>;
  /** Sibling actions rendered at the right edge of the row, after Fetch/Clear.
   *  Use for destructive admin actions (e.g. "Delete All") or bulk-selection
   *  actions (e.g. "Add to Dataset") that should be visually separated from
   *  the filter controls. */
  trailingActions?: ReactNode;
  /** Called when the Fetch button is clicked — fires after onChange. Use to commit drafts owned outside this component. */
  onFetch?: () => void;
  /** Show a subtle Fetch button style instead of the prominent primary-colored one. */
  subtle?: boolean;
  /** Disable the Fetch button (e.g. while a request is in flight). */
  loading?: boolean;
  loadingLabel?: string;
  /** Disable the Fetch button when no draft changes are pending (caller-controlled). */
  fetchDisabled?: boolean;
  /** Auto-apply mode: every change calls onChange immediately and the Fetch button is hidden.
   *  Use when filters should drive the query live (no commit step). */
  hideFetch?: boolean;
  /** Hide the Clear button. For a bar whose filters are all required — a scope
   *  selector rather than a search — "clear" has nothing to clear to: the view
   *  below cannot render without a value, so clearing only bounces back to the
   *  defaults and reads as a broken control. */
  hideClear?: boolean;
  /** Disable the Fetch button when drafts equal committed values (no pending changes).
   *  Useful for views that auto-load on mount, where Fetch is purely a "apply changes" affordance. */
  disableFetchWhenClean?: boolean;
  /**
   * Filters that commit on pick instead of waiting for Fetch.
   *
   * For a control that is the view's SCOPE rather than one of its filters — a
   * subject select whose value decides which topics, which checks and which
   * questions the rest of the bar can even offer. Held as a draft, such a
   * control silently lies: the select says Physics while everything beside it
   * is still answering about Chemistry, and nothing on screen says which one
   * the page is actually showing.
   *
   * Only the named key is committed — the other pending drafts stay pending, so
   * this does not turn a half-typed filter set into a query.
   */
  immediateKeys?: string[];
}

export function SearchFilterBar({
  filters,
  values,
  onChange,
  searchPlaceholder = 'Search...',
  hideSearch,
  leading,
  children,
  renderAfter,
  trailingActions,
  onFetch,
  subtle,
  loading,
  loadingLabel = 'Fetching...',
  fetchDisabled,
  hideFetch,
  hideClear,
  disableFetchWhenClean,
  immediateKeys,
}: SearchFilterBarProps) {
  const [draft, setDraft] = useState<Record<string, string>>({ ...values });

  /**
   * A filter committed from OUTSIDE the bar wins over its draft — for the keys
   * it actually changed, and no others.
   *
   * Without this the draft was seeded once and never looked at `values` again,
   * so a caller that clears its filters (as a subject change does — the old
   * topic and board mean nothing under the new subject) left every select still
   * showing the choice it had just dropped, and the next Fetch put it back.
   *
   * The delta rather than a wholesale resync: a pending draft the caller has not
   * spoken about is the reader mid-edit, and overwriting it on any re-render
   * would make the bar unusable.
   */
  const committed = useRef(values);
  useEffect(() => {
    const prev = committed.current;
    const changed = [...new Set([...Object.keys(prev), ...Object.keys(values)])]
      .filter(k => (prev[k] ?? '') !== (values[k] ?? ''));
    committed.current = values;
    if (changed.length === 0) return;
    setDraft(d => {
      const next = { ...d };
      for (const k of changed) next[k] = values[k] ?? '';
      return next;
    });
  }, [values]);

  const isDirty = Object.keys({ ...draft, ...values }).some(
    k => (draft[k] ?? '') !== (values[k] ?? ''),
  );

  const setFilter = (key: string, value: string) => {
    if (hideFetch) {
      const next = { ...draft, [key]: value };
      setDraft(next);
      onChange(next);
    } else if (immediateKeys?.includes(key)) {
      // COMMITTED values plus this one change — never the whole draft, which
      // would apply every other half-made choice as a side effect of picking a
      // scope.
      setDraft(prev => ({ ...prev, [key]: value }));
      onChange({ ...values, [key]: value });
    } else {
      setDraft(prev => ({ ...prev, [key]: value }));
    }
  };

  const apply = () => {
    onChange({ ...draft });
    onFetch?.();
  };

  /** A `required` filter is never "active" and is never cleared: its value is
   *  always one of its options, so there is nothing to clear it TO. Counting it
   *  left Clear permanently offering to put the view into a state the filter
   *  explicitly forbids. */
  const requiredKeys = new Set(
    filters.filter(f => (f.kind ?? 'select') === 'select' && (f as SelectFilterDef).required)
      .map(f => f.key),
  );
  const hasActive = Object.entries(draft).some(([k, v]) => v !== '' && !requiredKeys.has(k));

  const clearAll = () => {
    const cleared: Record<string, string> = {};
    for (const f of filters) {
      if (requiredKeys.has(f.key)) {
        cleared[f.key] = draft[f.key] ?? '';
      } else if (f.kind === 'daterange') {
        cleared[`${f.key}From`] = '';
        cleared[`${f.key}To`] = '';
      } else {
        cleared[f.key] = '';
      }
    }
    cleared.search = '';
    setDraft(cleared);
    onChange(cleared);
    onFetch?.();
  };

  return (
    <div className="sfb">
      {leading}
      {filters.map(f => {
        // Each control is built first, then paired with its optional
        // `renderAfter` slot so a custom input can sit beside the filter it
        // depends on instead of being pushed to the end of the row.
        const control = (() => {
        const kind = filterKind(f);
        const value = draft[f.key] || '';
        if (kind === 'select') {
          const sf = f as SelectFilterDef;
          return (
            <select
              key={f.key}
              className="sfb-select"
              value={value}
              onChange={e => setFilter(f.key, e.target.value)}
            >
              {!sf.required && <option value="">{sf.placeholder}</option>}
              {renderOptions(sf.options)}
            </select>
          );
        }
        if (kind === 'text') {
          const tf = f as TextFilterDef;
          return (
            <input
              key={f.key}
              type="text"
              className="sfb-text"
              placeholder={tf.placeholder}
              value={value}
              onChange={e => setFilter(f.key, e.target.value)}
              onKeyDown={e => { if (!hideFetch && e.key === 'Enter') apply(); }}
            />
          );
        }
        if (kind === 'date') {
          const df = f as DateFilterDef;
          return (
            <DatePicker
              key={f.key}
              className="sfb-date"
              title={df.title}
              ariaLabel={df.title}
              value={value}
              onChange={v => setFilter(f.key, v)}
            />
          );
        }
        if (kind === 'multiselect') {
          const mf = f as MultiSelectFilterDef;
          return (
            <MultiSelectDropdown
              key={f.key}
              def={mf}
              value={value}
              onChange={v => setFilter(f.key, v)}
            />
          );
        }
        if (kind === 'chips') {
          const cf = f as ChipsFilterDef;
          // Empty draft → fall through to the first option as the default.
          // Chip groups always have a value (no empty state), so we resolve
          // here rather than forcing every caller to seed `values[key]`.
          const resolved = value || cf.options[0]?.value || '';
          return (
            <div key={f.key} className="sfb-chips">
              {cf.label && <span className="sfb-chips-label">{cf.label}</span>}
              <ChipGroup
                value={resolved}
                options={cf.options}
                onChange={(v) => setFilter(f.key, v)}
                ariaLabel={cf.label}
              />
            </div>
          );
        }
        if (kind === 'daterange') {
          const drf = f as DateRangeFilterDef;
          const fromKey = `${f.key}From`;
          const toKey = `${f.key}To`;
          return (
            <div key={f.key} className="sfb-daterange">
              {drf.label && <span className="sfb-daterange-label">{drf.label}</span>}
              <DatePicker
                className="sfb-date"
                title={drf.fromTitle ?? 'From'}
                // Fall back to the title so a labelless range (icon + From/To
                // placeholders only) still has a meaningful accessible name.
                ariaLabel={drf.label ? `${drf.label} from` : (drf.fromTitle ?? 'From')}
                {...(drf.fromPlaceholder ? { placeholder: drf.fromPlaceholder } : {})}
                value={draft[fromKey] || ''}
                max={draft[toKey] || undefined}
                onChange={v => setFilter(fromKey, v)}
              />
              <span className="sfb-daterange-sep">–</span>
              <DatePicker
                className="sfb-date"
                title={drf.toTitle ?? 'To'}
                ariaLabel={drf.label ? `${drf.label} to` : (drf.toTitle ?? 'To')}
                {...(drf.toPlaceholder ? { placeholder: drf.toPlaceholder } : {})}
                value={draft[toKey] || ''}
                min={draft[fromKey] || undefined}
                onChange={v => setFilter(toKey, v)}
              />
            </div>
          );
        }
        // datetime-local
        const dtf = f as DateTimeLocalFilterDef;
        return (
          <input
            key={f.key}
            type="datetime-local"
            className="sfb-datetime"
            title={dtf.title}
            value={value}
            onChange={e => setFilter(f.key, e.target.value)}
          />
        );
        })();
        const slot = renderAfter?.[f.key];
        return slot
          ? <Fragment key={f.key}>{control}{slot}</Fragment>
          : control;
      })}
      {!hideSearch && (
        <input
          type="text"
          className="sfb-search"
          placeholder={searchPlaceholder}
          value={draft.search || ''}
          onChange={e => setFilter('search', e.target.value)}
          onKeyDown={e => { if (!hideFetch && e.key === 'Enter') apply(); }}
        />
      )}
      {children}
      {!hideFetch && (
        <button
          className={subtle ? 'sfb-fetch sfb-fetch--subtle' : 'sfb-fetch'}
          onClick={apply}
          type="button"
          disabled={loading || fetchDisabled || (disableFetchWhenClean && !isDirty)}
        >
          {loading ? loadingLabel : 'Fetch'}
        </button>
      )}
      {hasActive && !hideClear && (
        <button className="sfb-clear" onClick={clearAll} type="button">Clear</button>
      )}
      {trailingActions && (
        <div className="sfb-trailing">{trailingActions}</div>
      )}
    </div>
  );
}
